import { createReducer } from "@reduxjs/toolkit";
import {
  loginUser,
  loginSuccess,
  loginFailure,
  loadCurrentUser,
  loadCurrentUserSuccess,
  loadCurrentUserFailure,
  logout,
  refreshToken,
  refreshTokenSuccess,
  refreshTokenFailure,
  requestResetPassword,
  requestResetPasswordSuccess,
  requestResetPasswordFailure,
  clearForgotPasswordState,
} from "./actions";
import { AuthenticationState } from "./types";
import { LOCAL_STORAGE_KEYS } from "#root/utils/localStorage";
import { BROADCAST_CHANNEL_NAME, REFRESH_SESSION_MESSAGE, TAB_ID, broadcastToOtherTabs } from "#root/utils/session";
import { offlineOperationsService } from "#root/services/offlineOperationsService";

const clearLocalUserData = () => {
  localStorage.removeItem(LOCAL_STORAGE_KEYS.VTRACKER_SELECTED_INCIDENT);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.VADMIN_SELECTED_INCIDENT);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.CURRENT_USER_KEY);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.DEPARTMENT);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.UNIT_ID);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.TENANT_ID);
};

const initialState: AuthenticationState = {
  // Initialize auth state based on stored user data
  isAuthenticated: !!localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_USER_KEY),
  currentUserLoading: false,
  tokensLoading: false,
  error: null,
  currentUser: JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_USER_KEY) || 'null'),
  resetSuccess: false,
  resetRequestSuccess: false,
};

const authChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
authChannel.onmessage = (event) => {
  if (event.data.type === REFRESH_SESSION_MESSAGE && event.data.sourceTabId !== TAB_ID) {
    window.location.reload();
  }
};

export const authReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(loginUser, (state) => {
      state.currentUserLoading = true;
      state.error = null;
    })
    .addCase(loginSuccess, (state) => {
      broadcastToOtherTabs();
      state.isAuthenticated = true;
      state.currentUserLoading = false;
      state.error = null;
    })
    .addCase(loginFailure, (state, action) => {
      clearLocalUserData();
      state.isAuthenticated = false;
      state.currentUserLoading = false;
      state.error = action.payload.error;
      state.currentUser = null;
    })
    .addCase(loadCurrentUser, (state) => {
      state.currentUserLoading = true;
      state.error = null;
    })
    .addCase(loadCurrentUserSuccess, (state, action) => {
      state.isAuthenticated = true;
      state.currentUserLoading = false;
      state.currentUser = action.payload;
      state.error = null;
      localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_USER_KEY, JSON.stringify(action.payload));
      offlineOperationsService.processQueue();
    })
    .addCase(loadCurrentUserFailure, (state, action) => {
      clearLocalUserData();
      state.isAuthenticated = false;
      state.currentUserLoading = false;
      state.error = action.payload.error;
      state.currentUser = null;
    })
    .addCase(logout, (state) => {
      broadcastToOtherTabs();
      state.isAuthenticated = false;
      state.currentUserLoading = false;
      state.currentUser = null;
      clearLocalUserData();
    })
    .addCase(refreshToken, (state) => {
      state.tokensLoading = true;
      state.error = null;
    })
    .addCase(refreshTokenSuccess, (state) => {
      state.isAuthenticated = true;
      state.tokensLoading = false;
      state.error = null;
    })
    .addCase(refreshTokenFailure, (state, action) => {
      clearLocalUserData();
      state.isAuthenticated = false;
      state.tokensLoading = false;
      state.error = action.payload.error;
      state.currentUser = null;
    })


    // Password reset related; TODO: remove password reset from redux actions 
    .addCase(requestResetPassword, (state) => {
      state.currentUserLoading = true;
      state.error = null;
      state.resetRequestSuccess = false;
    })
    .addCase(requestResetPasswordSuccess, (state) => {
      clearLocalUserData();
      state.currentUserLoading = false;
      state.resetRequestSuccess = true;
    })
    .addCase(requestResetPasswordFailure, (state, action) => {
      clearLocalUserData();
      state.currentUserLoading = false;
      state.error = action.payload.error;
      state.resetRequestSuccess = false;
    })
    .addCase(clearForgotPasswordState, (state) => {
      state.resetRequestSuccess = false;
      state.error = null;
    });
});
